import { FC, useCallback, useContext } from 'react';
import { Checkbox, Flex } from 'antd';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Position } from '@xyflow/react';
import {
  AddRemoveButtons,
  TextEllipsis,
} from '../../../../../../common/components';
import { FormField, FormSelect } from '../../../../../../common/form';
import { FormSelectSelectionLabel } from '../../../../../../common/form/input/FormSelectSelectionLabel';
import { formatCodeName, type UUIDType } from '../../../../../../common/utils';
import { AlgorithmGraphReadOnlyField } from '../../../../AlgorithmGraphReadOnlyField';
import { AlgorithmSourceFormDataType } from '../../../../sources/schema/types';
import { createAlgorithmSourceDimensionsOptionGroup } from '../../../../sources/utils';
import { getAlgorithmSourceLabel } from '../../../../utils';
import type {
  AlgorithmJoinsAndMappingsGraphNodeItemType,
  AlgorithmJoinsAndMappingsGraphValidFormDataType,
} from '../../../schema/types';
import {
  type AlgorithmJoinHandleId,
  decodeDimensionCodeValue,
  encodeDimensionCodeValue,
  getHandleId,
  parseHandleId,
} from '../../../utils';
import { AlgorithmJoinsWithMappingsGraphContext } from '../../AlgorithmJoinsWithMappingsGraphContext';
import { useConnection } from '../../useConnection';
import { AlgorithmJoinsAndMappingsGraphHandle } from '../AlgorithmJoinsAndMappingsGraphHandle';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { ConstantFormField } from '../ConstantFormField';
import translations from './AlgorithmSourceNodeItem.translations.json';
import { DbSourceDimensionColumnFormInput } from './DbSourceDimensionColumnFormInput';
import { formatConstantDisplayValue } from '../formatConstantDisplayValue';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-source-node-item',
);
addTranslations(translations);

export interface AlgorithmSourceNodeItemProps {
  nodeId: UUIDType;
  algorithmSource: AlgorithmSourceFormDataType;
  item: AlgorithmJoinsAndMappingsGraphNodeItemType;
  itemIndex: number;
  baseName: `nodes.${number}.data`;
  isRoot?: boolean;
  isLast?: boolean;
}

export const AlgorithmSourceNodeItem: FC<AlgorithmSourceNodeItemProps> = (
  props,
) => {
  const { nodeId, algorithmSource, item, itemIndex, baseName, isRoot, isLast } =
    props;
  const { t } = useTranslation();
  const isConstant = 'constantValue' in item;
  const label = useAlgorithmSourceNodeItemLabel(nodeId, itemIndex, isConstant);
  const { isDbSources, readOnly } = useContext(
    AlgorithmJoinsWithMappingsGraphContext,
  );
  const { getValues, setValue } =
    useFormContext<AlgorithmJoinsAndMappingsGraphValidFormDataType>();

  const sourceConnection = useConnection(nodeId, itemIndex, 'target');
  const targetConnection = useConnection(nodeId, itemIndex, 'source');

  const dropConstantConnection = useCallback(() => {
    if (!sourceConnection && !targetConnection) {
      return;
    }

    const nodes = getValues('nodes');
    const edges = getValues('edges');
    const dropEdge = (edgeId: string) =>
      setValue(
        'edges',
        edges.filter(({ id }) => id !== edgeId),
        { shouldDirty: true },
      );

    if (sourceConnection) {
      const sourceNode = nodes.find(({ id }) => id === sourceConnection.source);

      if (sourceNode?.type === 'constant') {
        dropEdge(sourceConnection.edgeId);
      } else if (isDbSources && sourceNode?.type === 'algorithmSource') {
        const { itemIndex: sourceItemIndex } = parseHandleId(
          sourceConnection.sourceHandle as AlgorithmJoinHandleId,
        );
        const sourceItem = sourceNode.data.items[sourceItemIndex];

        if ('constantValue' in sourceItem) {
          dropEdge(sourceConnection.edgeId);
        }
      }
    } else if (targetConnection) {
      // constant item under algorithm source can be target only (source constants are in separate constant nodes)
      dropEdge(targetConnection.edgeId);
    }
  }, [sourceConnection, targetConnection, getValues, isDbSources, setValue]);

  const removeItem = useCallback(() => {
    setValue(
      `${baseName}.items`,
      getValues(`${baseName}.items`).filter((_, i) => i !== itemIndex),
      { shouldDirty: true },
    );

    if (isLast) {
      return;
    }

    // When removing an item that is not the last one, then update all the edges
    // that are connected to subsequent items: replace them with new edges that
    // have a new id and item index in the handle id is decremented by 1.
    setValue(
      'edges',
      getValues('edges').map((edge) => {
        const isSource = edge.source === nodeId;
        const isTarget = edge.target === nodeId;
        let edgeItemIndex: number;

        if (
          (!isSource && !isTarget) ||
          (edgeItemIndex = parseHandleId(
            isSource ? edge.sourceHandle : edge.targetHandle,
          ).itemIndex) <= itemIndex
        ) {
          return edge;
        }

        return {
          ...edge,
          id: window.crypto.randomUUID(),
          ...(isSource
            ? { sourceHandle: getHandleId(nodeId, edgeItemIndex - 1, 'source') }
            : {
                targetHandle: getHandleId(nodeId, edgeItemIndex - 1, 'target'),
              }),
        };
      }),
      { shouldDirty: true },
    );
  }, [setValue, baseName, getValues, isLast, itemIndex, nodeId]);

  const itemBaseName = `${baseName}.items.${itemIndex}` as const;
  const name = isConstant
    ? (`${itemBaseName}.constantValue` as const)
    : (`${itemBaseName}.dimensionCode` as const);

  if (readOnly) {
    return (
      <AlgorithmGraphReadOnlyField
        label={label}
        style={{ position: 'relative' }}
      >
        {isConstant ? (
          <TextEllipsis>
            {formatConstantDisplayValue(item.constantValue)}
          </TextEllipsis>
        ) : isDbSources ? (
          <TextEllipsis>
            <DbSourceDimensionColumnFormInput
              name={
                name as `nodes.${number}.data.items.${number}.dimensionCode`
              }
              value={item.dimensionCode}
              algorithmSource={algorithmSource}
              readOnly
            />
            <AlgorithmJoinsAndMappingsGraphHandle
              nodeId={nodeId}
              itemIndex={itemIndex}
              type="source"
              position={Position.Right}
            />
          </TextEllipsis>
        ) : (
          <>
            <FormSelectSelectionLabel
              ellipsis
              supertitle={getAlgorithmSourceLabel(algorithmSource)}
            >
              {formatCodeName(
                algorithmSource.dimensions.find(
                  (d) =>
                    d.code ===
                    decodeDimensionCodeValue(item.dimensionCode).dimensionCode,
                ),
              )}
            </FormSelectSelectionLabel>
            <AlgorithmJoinsAndMappingsGraphHandle
              nodeId={nodeId}
              itemIndex={itemIndex}
              type="source"
              position={Position.Right}
            />
          </>
        )}
        {!isRoot && (
          <AlgorithmJoinsAndMappingsGraphHandle
            nodeId={nodeId}
            itemIndex={itemIndex}
            type="target"
            position={Position.Left}
          />
        )}
      </AlgorithmGraphReadOnlyField>
    );
  }

  return (
    <FormField
      key={name}
      name={name}
      required
      style={{ minWidth: 250 }}
      label={
        <>
          {label}
          <Flex gap="small" style={{ float: 'right' }}>
            {!isRoot && (
              <Checkbox
                id={`${itemBaseName.replace(/\./g, '-')}-toggle-constant`}
                checked={isConstant}
                onChange={(event) => {
                  const { checked } = event.target;
                  if (!checked) {
                    setValue(
                      itemBaseName,
                      { dimensionCode: '' },
                      { shouldDirty: true },
                    );
                    return;
                  }
                  dropConstantConnection();
                  setTimeout(() => {
                    setValue(
                      itemBaseName,
                      { constantValue: '' },
                      { shouldDirty: true },
                    );
                  });
                }}
              >
                {t('constant')}
              </Checkbox>
            )}
            <AddRemoveButtons
              id={`${itemBaseName.replace(/\./g, '-')}-remove`}
              onClickRemove={() => {
                removeItem();
              }}
            />
          </Flex>
        </>
      }
    >
      <ItemFieldWrapper>
        {isConstant ? (
          <ConstantFormField
            type="target"
            nodeId={nodeId}
            itemIndex={itemIndex}
            name={name as `nodes.${number}.data.items.${number}.constantValue`}
          />
        ) : isDbSources ? (
          <>
            <DbSourceDimensionColumnFormInput
              name={
                name as `nodes.${number}.data.items.${number}.dimensionCode`
              }
              value={item.dimensionCode}
              algorithmSource={algorithmSource}
            />
            <AlgorithmJoinsAndMappingsGraphHandle
              nodeId={nodeId}
              itemIndex={itemIndex}
              type="source"
              position={Position.Right}
            />
          </>
        ) : (
          <>
            <FormSelect
              name={name}
              className="nodrag"
              optionLabelProp="displayLabel"
              options={
                algorithmSource
                  ? [
                      createAlgorithmSourceDimensionsOptionGroup(
                        algorithmSource,
                        -1,
                        (algorithmSource, dimension) =>
                          encodeDimensionCodeValue(
                            algorithmSource.id,
                            dimension.code,
                          ),
                      ),
                    ]
                  : undefined
              }
              allowClear
            />
            <AlgorithmJoinsAndMappingsGraphHandle
              nodeId={nodeId}
              itemIndex={itemIndex}
              type="source"
              position={Position.Right}
            />
          </>
        )}
        {!isRoot && (
          <AlgorithmJoinsAndMappingsGraphHandle
            nodeId={nodeId}
            itemIndex={itemIndex}
            type="target"
            position={Position.Left}
          />
        )}
      </ItemFieldWrapper>
    </FormField>
  );
};

const ItemFieldWrapper = styled(Flex)`
  position: relative;
  width: 100%;
`;

const useAlgorithmSourceNodeItemLabel = (
  nodeId: UUIDType,
  itemIndex: number,
  isConstant: boolean,
) => {
  const { t } = useTranslation();
  const isConnectionSource = useConnection(nodeId, itemIndex, 'source');
  const isConnectionTarget = useConnection(nodeId, itemIndex, 'target');
  return t(
    isConstant
      ? 'constant-2'
      : isConnectionSource
        ? 'dimension-1'
        : isConnectionTarget
          ? 'dimension-2'
          : 'dimension',
  );
};
